import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Alert,
} from '@mui/material';
import {
    Upgrade,
    CheckCircle,
    TrendingUp,
    Campaign,
    Message,
    AccountCircle,
    SmartToy,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const PLAN_LIMITS = {
    free: {
        messagesPerMonth: 100,
        campaignsTotal: 2,
        accountsPerPlatform: 1,
        chatbotRules: 5,
    },
    pro: {
        messagesPerMonth: 5000,
        campaignsTotal: 20,
        accountsPerPlatform: 5,
        chatbotRules: 50,
    },
    enterprise: {
        messagesPerMonth: -1,
        campaignsTotal: -1,
        accountsPerPlatform: -1,
        chatbotRules: -1,
    },
};

const BillingDashboard = () => {
    const { user } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [usage, setUsage] = useState(null);

    const currentPlan = user?.subscription?.plan || 'free';
    const limits = PLAN_LIMITS[currentPlan];

    useEffect(() => {
        fetchUsage();
    }, []);

    const fetchUsage = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsage(data.usage);
        } catch (error) {
            console.error('Error fetching usage:', error);
        }
    };

    const getUsagePercentage = (current, limit) => {
        if (limit === -1) return 0; // Unlimited
        return Math.min((current / limit) * 100, 100);
    };

    const getProgressColor = (percentage) => {
        if (percentage < 50) return 'success';
        if (percentage < 80) return 'warning';
        return 'error';
    };

    const handleUpgrade = async (plan) => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.post(
                `${API_BASE_URL}/api/payment/request`,
                { requestedPlan: plan },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            // Show WhatsApp contact info
            alert(`✅ تم إنشاء طلب الترقية!\n\n📱 للتفعيل:\n1. حوّل مبلغ $${data.request.amount}\n2. تواصل معنا عبر واتساب: ${data.whatsappNumber}\n3. أرسل إثبات الدفع\n4. سيتم تفعيل حسابك خلال 24 ساعة`);

            fetchUsage();
        } catch (error) {
            if (error.response?.data?.message) {
                alert(error.response.data.message);
            } else {
                alert('حدث خطأ. يرجى المحاولة مرة أخرى.');
            }
        }
    };

    const planColors = {
        free: '#757575',
        pro: '#1976d2',
        enterprise: '#9c27b0',
    };

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
            <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
            >
                الاشتراك والفواتير
            </Typography>

            {/* Current Plan */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CheckCircle sx={{ color: planColors[currentPlan], mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    الخطة الحالية
                                </Typography>
                            </Box>
                            <Chip
                                label={currentPlan.toUpperCase()}
                                sx={{
                                    bgcolor: planColors[currentPlan],
                                    color: 'white',
                                    fontWeight: 'bold',
                                    mb: 2,
                                }}
                            />
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {currentPlan === 'free' && 'خطة مجانية مدى الحياة'}
                                {currentPlan === 'pro' && '$29/شهر - خطة احترافية'}
                                {currentPlan === 'enterprise' && '$99/شهر - خطة الشركات'}
                            </Typography>
                            {currentPlan !== 'enterprise' && (
                                <Button
                                    variant="contained"
                                    startIcon={<Upgrade />}
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    onClick={() => handleUpgrade(currentPlan === 'free' ? 'pro' : 'enterprise')}
                                >
                                    ترقية الخطة
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6" fontWeight="bold">
                                    ملخص الاستخدام
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        الرسائل المرسلة
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {usage?.messagesSent || 0}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        الحملات النشطة
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {usage?.campaignsCreated || 0}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        الحسابات المتصلة
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {usage?.accountsConnected || 0}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        قواعد الشات بوت
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {usage?.chatbotRulesCreated || 0}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Usage Limits */}
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                حدود الاستخدام
            </Typography>
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Message sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">الرسائل الشهرية</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {usage?.messagesSent || 0} / {limits.messagesPerMonth === -1 ? '∞' : limits.messagesPerMonth}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {limits.messagesPerMonth === -1
                                        ? 'غير محدود'
                                        : `${getUsagePercentage(usage?.messagesSent || 0, limits.messagesPerMonth).toFixed(0)}%`}
                                </Typography>
                            </Box>
                            {limits.messagesPerMonth !== -1 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={getUsagePercentage(usage?.messagesSent || 0, limits.messagesPerMonth)}
                                    color={getProgressColor(getUsagePercentage(usage?.messagesSent || 0, limits.messagesPerMonth))}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Campaign sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">الحملات</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {usage?.campaignsCreated || 0} / {limits.campaignsTotal === -1 ? '∞' : limits.campaignsTotal}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {limits.campaignsTotal === -1
                                        ? 'غير محدود'
                                        : `${getUsagePercentage(usage?.campaignsCreated || 0, limits.campaignsTotal).toFixed(0)}%`}
                                </Typography>
                            </Box>
                            {limits.campaignsTotal !== -1 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={getUsagePercentage(usage?.campaignsCreated || 0, limits.campaignsTotal)}
                                    color={getProgressColor(getUsagePercentage(usage?.campaignsCreated || 0, limits.campaignsTotal))}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <AccountCircle sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">الحسابات المتصلة</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {usage?.accountsConnected || 0} / {limits.accountsPerPlatform === -1 ? '∞' : limits.accountsPerPlatform}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {limits.accountsPerPlatform === -1
                                        ? 'غير محدود'
                                        : `${getUsagePercentage(usage?.accountsConnected || 0, limits.accountsPerPlatform).toFixed(0)}%`}
                                </Typography>
                            </Box>
                            {limits.accountsPerPlatform !== -1 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={getUsagePercentage(usage?.accountsConnected || 0, limits.accountsPerPlatform)}
                                    color={getProgressColor(getUsagePercentage(usage?.accountsConnected || 0, limits.accountsPerPlatform))}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SmartToy sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">قواعد الشات بوت</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2">
                                    {usage?.chatbotRulesCreated || 0} / {limits.chatbotRules === -1 ? '∞' : limits.chatbotRules}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {limits.chatbotRules === -1
                                        ? 'غير محدود'
                                        : `${getUsagePercentage(usage?.chatbotRulesCreated || 0, limits.chatbotRules).toFixed(0)}%`}
                                </Typography>
                            </Box>
                            {limits.chatbotRules !== -1 && (
                                <LinearProgress
                                    variant="determinate"
                                    value={getUsagePercentage(usage?.chatbotRulesCreated || 0, limits.chatbotRules)}
                                    color={getProgressColor(getUsagePercentage(usage?.chatbotRulesCreated || 0, limits.chatbotRules))}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Upgrade CTA */}
            {currentPlan === 'free' && (
                <Alert severity="info" sx={{ mb: 4 }}>
                    <Typography variant="body1" fontWeight="bold" gutterBottom>
                        هل تحتاج المزيد؟
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        قم بالترقية إلى خطة Pro للحصول على 5,000 رسالة شهرياً و 20 حملة تسويقية!
                    </Typography>
                    <Button variant="contained" size="small" sx={{ mt: 1 }} onClick={() => handleUpgrade('pro')}>
                        ترقية الآن
                    </Button>
                </Alert>
            )}
        </Container>
    );
};

export default BillingDashboard;
